import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import RadioButton from 'components/buttons/RadioButton';
import _ from 'lodash';
import styles from '../MockParameters.module.css';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import InputGroup from 'react-bootstrap/InputGroup';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import CertificateSelector from 'components/certUploader/CertificateSelector';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import { REGEX_VALID_HTTP_URL } from 'utils/regexUtils';
import { AssetAssignmentBundle, SslConfig, SSlConfigMode } from '../SslConfiguration';
import {
  addCertificateToAssetBundle,
  CertAssignment,
  getSelectedKeysFromAssetBundle,
  updateConfigurablePropertyInBundle,
} from 'utils/AssetBundleUtils';

interface NoMatchRadioGroupProps {
  id: number;
  liveSystemHost: string;
  assetBundle: AssetAssignmentBundle;
  onUpdateLiveSystemHost: (value: string | number) => void;
  noMatchingRequestPreference: string;
  onNoMatchReqPrefChange: (value: string) => void;
  disabled: boolean;
  onAssetBundleChanged: (assets: AssetAssignmentBundle) => void;
}

const NoMatchRadioGroup = (props: NoMatchRadioGroupProps) => {
  /**
   * Use effect for applying changes coming from outside.
   * */
  useEffect(() => {
    if (liveSystemInput.value !== props.liveSystemHost) {
      liveSystemInput.resetValue(props.liveSystemHost);
    }
  }, [props.liveSystemHost]);

  const { t } = useTranslation('mockServices');

  /**
   * Get cert id from assigned assets - there can be multiple assets assigned to the virtual service, so
   * we need to select the one for redirect to live.
   */
  const selectedCertificateKeys = getSelectedKeysFromAssetBundle(
    props.assetBundle,
    SslConfig.ONE_WAY,
    SSlConfigMode.CLIENT,
  );

  const liveSystemInput = useFormInput(
    props.liveSystemHost,
    (fn: CallbackFn) => {
      if (fn.isValid) {
        props.onUpdateLiveSystemHost(fn.value);
      }
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('mockServices:errorMsgs.errorLiveSystemEndpointEmpty'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      value: REGEX_VALID_HTTP_URL,
      invalidMessage: i18n.t('mockServices:errorMsgs.errorLiveSystemEndpoint'),
    },
  );

  return (
    <>
      {/* No Matching Request */}
      <Form.Group>
        <div className={'mb-3'}>
          <RadioButton
            disabled={props.disabled}
            isChecked={props.noMatchingRequestPreference === 'return404' || _.isNil(props.noMatchingRequestPreference)}
            id={'return404'}
            label={t('parametersTab.returnNoMatchFound')}
            onChange={() => props.onNoMatchReqPrefChange('return404')}
          />
        </div>
      </Form.Group>
      {/* ./No Matching Request */}

      {/* Bypass Live */}
      <div className={'mb-3'}>
        <RadioButton
          disabled={props.disabled}
          isChecked={props.noMatchingRequestPreference === 'bypasslive'}
          id={'redirectToLive'}
          label={t('parametersTab.redirectToLiveSystem')}
          onChange={() => props.onNoMatchReqPrefChange('bypasslive')}
        />

        {props.noMatchingRequestPreference === 'bypasslive' && (
          <div className={'pl-5'}>
            {/* Live System Endpoint */}
            <Form.Group className={'mb-4'} controlId={'qa-params-live-system-endpoint' + props.id}>
              <Form.Label>{t('parametersTab.liveSystemEndpoint')}</Form.Label>
              <span id="liveEndpointHelp" className={styles.questionInTableHeader} title={t('common:showHelp')}>
                <HelpTooltip id={'live_system_endpoint_help'} />
              </span>
              <InputGroup className="mb-3">
                <BzFormControlWithHook
                  disabled={props.disabled}
                  useFormInputHook={liveSystemInput}
                  placeholder="http://mylivesystemendpoint"
                  required={false}
                />
              </InputGroup>
            </Form.Group>
            {/* ./Live System Endpoint */}

            {/* Cert Selector */}
            <CertificateSelector
              allowUnSelect={true}
              type={'CERTIFICATE'}
              selectedCertificateId={selectedCertificateKeys.certificateId}
              disabled={props.disabled}
              onRemoveCertificate={id => {
                const filteredAssets = _.filter(props.assetBundle.assets, asset => asset.assetId !== id);
                props.onAssetBundleChanged({ assets: filteredAssets });
              }}
              onSelectCertificate={(certId, certName) => {
                //-2 means go back to no selection (certificate of keystore)
                if (certId === -2) {
                  props.onAssetBundleChanged({ assets: [], configKeyAssets: [] });
                  return;
                }
                const cert: CertAssignment = {
                  certId,
                  certificateName: certName,
                  usageType: 'CLIENT_TRUSTSTORE_CERT',
                };
                const updatedBundle = addCertificateToAssetBundle(props.assetBundle, cert);
                props.onAssetBundleChanged(updatedBundle);
              }}
              allowConfigurationProperty={true}
              onUpdateConfigPropertyValue={value => {
                const updatedBundle = updateConfigurablePropertyInBundle(
                  props.assetBundle,
                  value,
                  'CLIENT_TRUSTSTORE_CERT',
                );
                props.onAssetBundleChanged(updatedBundle);
              }}
              configPropertyValue={selectedCertificateKeys.configKeyValue}
            />
          </div>
        )}
      </div>
      {/* Bypass Live */}
    </>
  );
};

export default NoMatchRadioGroup;
